import React from 'react'
import { usePostHog } from '@posthog/react'

const imgImage = '/Image.jpg'
const imgLaurelWreathLeft01 = '/icon/laurel-wreath-left-01.svg'
const imgAward05 = '/icon/award-05.svg'
const imgCheckmarkCircle03 = '/icon/checkmark-circle-03.svg'
const imgCheckmarkCircleGreen = '/icon/checkmark-circle-green.svg'
const imgCustomerService02 = '/icon/customer-service-02.svg'

const metrics = [
  { icon: imgAward05, label: '99.2% Delivery' },
  { icon: imgCheckmarkCircle03, label: '5 min Setup' },
  { icon: imgCustomerService02, label: '24/7 Support' },
]

const Hero = () => {
  const posthog = usePostHog()

  const openEmailModal = () => {
    posthog?.capture('hero_cta_clicked')
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('openEmailModal', { detail: { source: 'hero_cta' } }))
    }
  }

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white via-[#f6fbf8] to-[#dffae8]">
      {/* subtle dotted texture */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(28,35,41,0.08) 1.1px, transparent 0)',
          backgroundSize: '22px 22px',
          maskImage:
            'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.55) 38%, rgba(255,255,255,0.2) 70%, rgba(255,255,255,0.02) 100%)',
        }}
        aria-hidden="true"
      />
      {/* green glow to bottom-right */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(0,216,118,0.22),transparent_45%)]" />

      <div className="relative mx-auto flex max-w-[1200px] flex-col items-center px-5 pb-12 pt-12 text-center sm:px-8 sm:pb-14 sm:pt-14 lg:px-[92px] lg:pb-16 lg:pt-16">
        {/* Pill */}
        <div className="flex items-center gap-2 rounded-full bg-white px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.28px] text-[#1c2329] shadow-[inset_0px_1px_2px_rgba(26,32,50,0.08),0px_6px_16px_rgba(28,35,41,0.05)] sm:px-4 sm:text-[11px]">
          <img src={imgLaurelWreathLeft01} alt="" className="h-5 w-5 sm:h-6 sm:w-6" />
          <span>Trusted by 1000+ businesses</span>
        </div>

        {/* Heading */}
        <h1 className="mt-6 max-w-4xl text-[32px] font-semibold leading-[1.12] tracking-[-1px] text-[#1c2329] sm:text-[40px] sm:leading-[1.1] sm:tracking-[-1.1px] md:text-[52px] md:leading-[1.08] md:tracking-[-1.2px]">
          Convert more leads on{' '}
          <span className="text-[#00c86b]">WhatsApp</span>
        </h1>

        {/* Features List */}
        <div className="mt-4 flex max-w-[680px] flex-col items-center gap-3 sm:gap-4">
          <div
            className="flex items-center gap-3 justify-center opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.1s', animationDuration: '0.6s' }}
          >
            <img
              src={imgCheckmarkCircleGreen}
              alt=""
              className="h-5 w-5 flex-shrink-0 transition-transform duration-300 hover:scale-110 sm:h-6 sm:w-6"
            />
            <span className="text-[13px] leading-[1.6] tracking-[-0.28px] text-[#4b5650] sm:text-[17px] sm:leading-[1.7] sm:tracking-[-0.4px]">
              Send automatic messages and follow-ups to leads
            </span>
          </div>
          <div
            className="flex items-center gap-3 justify-center opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.25s', animationDuration: '0.6s' }}
          >
            <img
              src={imgCheckmarkCircleGreen}
              alt=""
              className="h-5 w-5 flex-shrink-0 transition-transform duration-300 hover:scale-110 sm:h-6 sm:w-6"
            />
            <span className="text-[13px] leading-[1.6] tracking-[-0.28px] text-[#4b5650] sm:text-[17px] sm:leading-[1.7] sm:tracking-[-0.4px]">
              Bulk message campaigns with deep analytics
            </span>
          </div>
          <div
            className="flex items-center gap-3 justify-center opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.4s', animationDuration: '0.6s' }}
          >
            <img
              src={imgCheckmarkCircleGreen}
              alt=""
              className="h-5 w-5 flex-shrink-0 transition-transform duration-300 hover:scale-110 sm:h-6 sm:w-6"
            />
            <span className="text-[13px] leading-[1.6] tracking-[-0.28px] text-[#4b5650] sm:text-[17px] sm:leading-[1.7] sm:tracking-[-0.4px]">
              AI Chatbots that let humans take over
            </span>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 w-full max-w-sm sm:max-w-none">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              openEmailModal()
            }}
            className="w-full rounded-full bg-[#0f1922] px-8 py-4 text-[15px] font-semibold tracking-[-0.5px] text-white shadow-[0px_10px_28px_rgba(15,25,34,0.25)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#0b131a] sm:w-auto sm:px-10 sm:text-[16px] inline-flex items-center justify-center"
          >
            Get Started
          </a>
        </div>

        {/* Money Back Guarantee */}
        <div className="mt-2.5 flex items-center justify-center sm:mt-3">
          <div className="inline-flex items-center rounded-full bg-gradient-to-r from-[#e8faf0] to-[#f0fdf4] px-3.5 py-1.5 shadow-[0px_2px_6px_rgba(0,200,107,0.1)] sm:px-4 sm:py-2">
            <p className="text-[12px] leading-tight tracking-[-0.15px] text-[#1c2329] sm:text-[13px] sm:tracking-[-0.2px]">
              <span className="font-semibold text-[#00c86b]">15 day</span>{' '}
              <span className="text-[#4b5650]">money back guarantee</span>
            </p>
          </div>
        </div>

        {/* Metrics */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[14.5px] tracking-[-0.44px] text-[#4b5650] sm:mt-7 sm:text-[16px] sm:tracking-[-0.48px]">
          {metrics.map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <img src={item.icon} alt="" className="h-5 w-5 sm:h-6 sm:w-6" />
              <span>{item.label}</span>
            </div>
          ))}
        </div>

        {/* Human support reassurance */}
        <p className="mt-5 flex flex-wrap items-center justify-center gap-2 text-center text-[13px] leading-[1.5] tracking-[-0.28px] text-[#4b5650] sm:text-[15px] sm:leading-[1.6] sm:tracking-[-0.36px]">
          <span className="inline-flex items-center gap-1.5">
            <img src={imgCustomerService02} alt="" className="h-4 w-4 flex-shrink-0 text-[#00c86b] sm:h-[18px] sm:w-[18px]" aria-hidden />
            <span className="font-medium text-[#1c2329]">100% human support</span>
            <span>- no AI, no bots.</span>
          </span>
          <span>Need help? <span className="font-semibold text-[#00c86b]">Just give us a call.</span></span>
        </p>

        {/* Meta Tech Provider, Pipedrive Partner & Trustpilot */}
        <div className="mt-6 flex flex-col items-center justify-center gap-4 sm:mt-8">
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8">
            <img src="/meta_tech_provider.svg" alt="Meta Tech Provider" className="h-8 w-auto sm:h-9" />
            <a
              href="https://www.pipedrive.com/en/marketplace/app/whats-able-whats-app-automation-with-pipedrive-ai/3be2f03344cc1c9f"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-opacity hover:opacity-80"
            >
              <img src="/pipedrive-partner.svg" alt="Pipedrive Partner" className="h-9 w-auto sm:h-10" />
            </a>
          </div>
          <a
            href="https://www.google.com/search?q=whatsable+trustpilot&ie=UTF-8"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-opacity hover:opacity-80"
          >
            <img src="/trustpilot.svg" alt="Trustpilot" className="h-auto w-auto max-w-[180px] sm:max-w-[220px]" />
          </a>
        </div>

        {/* Dashboard video */}
        <div className="relative mt-10 w-full max-w-[1160px] overflow-hidden rounded-[18px] bg-gradient-to-br from-[#dffae8] via-white to-[#dffae8] p-[10px] shadow-[0px_18px_70px_rgba(0,172,111,0.22)] sm:mt-12 sm:rounded-[22px] sm:p-[12px] sm:shadow-[0px_24px_90px_rgba(0,172,111,0.25)]">
          <div className="overflow-hidden rounded-[12px] border border-[#e5efe9] bg-white shadow-[0px_2px_12px_rgba(28,35,41,0.15)] sm:rounded-[14px]">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/TnxoYjb0pVE"
                title="Notifyer dashboard preview"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Hero
